import React from "react";
import { Autocomplete, Box, Checkbox, Chip, TextField, Typography } from "@mui/material";
import { Controller } from "react-hook-form";

interface Props {
  label?: string;
  name: string;
  size?: "small" | "medium";
  control: any;
  options: Array<{
    label: string;
    value: any;
    attendance?: any;
    isHoliday?: boolean;
    holidayType?: string;
    createTask: boolean,
  }>;
  multiple?: boolean;
  trigger?: () => void;
  required?: boolean;
  disabled?: boolean;
}

function getMemberBadge(attendance: any, isHoliday?: boolean) {
  let label = "NM";
  let color = "#777";

  // Holiday first
  if (isHoliday) {
    label = "H";
    color = "#FFD700";
  }

  // Attendance overrides holiday
  if (attendance) {
    if (
      attendance.type === "Present" ||
      (attendance.type === "Holiday" && attendance.checkin_time) ||
      (attendance.type === "Leave" && attendance.checkin_time)
    ) {
      label = "P";
      color = "#13B26B";
    } else if (attendance.type === "Absent") {
      label = "A";
      color = "#EF4444";
    } else if (attendance.type === "Leave") {
      label = "L";
      color = "#8B5CF6";
    } else if (attendance.type === "Holiday") {
      label = "H";
      color = "#FFD700";
    }
  }

  return { label, color };
}

function FormAutoCompleteWithSelectAll(props: Props) {
  const {
    name,
    size = "small",
    control,
    label = "",
    options,
    multiple = false,
    trigger,
    required = false,
    disabled = false,
  } = props;

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const isAllSelected =
            field.value?.length === options.length && options.length > 0;
          const handleSelectAll = () => {
            const newValue = isAllSelected ? [] : options;
            field.onChange(newValue);
            if (trigger) {
              trigger();
            }
          };

          return (
            <>
              <Autocomplete
                id={name}
                disabled={disabled}
                size={size}
                multiple
                disablePortal
                disableCloseOnSelect // Keeps the dropdown open when selecting options
                onChange={(_, value) => {
                  field.onChange(value);
                  if (trigger) {
                    trigger();
                  }
                }}
                value={field.value || []}
                options={[{ label: "Select All", value: "selectAll" }, ...options]}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option) => option.label}
                fullWidth
                renderOption={(props, option, { selected }) => {
                  if (option.value === "selectAll") {
                    return (
                      <li
                        {...props}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSelectAll();
                        }}
                      >
                        <Checkbox checked={isAllSelected} style={{ marginRight: 8 }} />
                        Select All
                      </li>
                    );
                  }

                  // A date can carry two attendance rows (first_half + second_half) - accept
                  // either the legacy single object (other callers of this shared component)
                  // or the full array (CreateTask's "Users *" field) without breaking either.
                  const attendanceRows: any[] = Array.isArray(option.attendance)
                    ? option.attendance
                    : option.attendance
                      ? [option.attendance]
                      : [];
                  const isHoliday = option.isHoliday;
                  const createTask = option.createTask;

                  const firstHalfRow = attendanceRows.find((a) => a?.dayType === "first_half");
                  const secondHalfRow = attendanceRows.find((a) => a?.dayType === "second_half");
                  const isSplitDay = !isHoliday && !!(firstHalfRow || secondHalfRow);

                  const singleBadge = getMemberBadge(attendanceRows[0], isHoliday);

                  const hasRejectedLeave = attendanceRows.some(
                    (a) => a?.type === "Leave" && a?.status === "REJECTED"
                  );

                  return (
                    <li {...props}>
                      <Checkbox checked={selected} style={{ marginRight: 8 }} />

                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <span>{option.label}</span>

                        {createTask && (
                          isSplitDay ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {[firstHalfRow || { type: "Absent" }, secondHalfRow || { type: "Absent" }].map((half, idx) => {
                                const badge = getMemberBadge(half, false);
                                return (
                                  <Box key={idx} display="flex" alignItems="center" gap={0.5}>
                                    {idx === 1 && <Typography sx={{ fontSize: 10, color: "#bbb", fontWeight: 700 }}>|</Typography>}
                                    <Box
                                      sx={{
                                        fontSize: "10px",
                                        fontWeight: 600,
                                        backgroundColor: badge.color,
                                        color: "#fff",
                                        borderRadius: "4px",
                                        px: "6px",
                                        py: "2px",
                                        minWidth: "20px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {badge.label}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                fontSize: "10px",
                                fontWeight: 600,
                                backgroundColor: singleBadge.color,
                                color: "#fff",
                                borderRadius: "4px",
                                px: "6px",
                                py: "2px",
                                minWidth: "20px",
                                textAlign: "center",
                              }}
                              title={option.holidayType || ""}
                            >
                              {singleBadge.label}
                            </Box>
                          )
                        )}
                      </Box>
                      {hasRejectedLeave && (
                        <span style={{ color: "#EF4444", fontSize: "15px", marginLeft: "5px", fontWeight: 600 }}>
                          ✖
                        </span>
                      )}
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) => {
                  const isAllSelected = value.length === options.length;

                  return (isAllSelected && label == "Client Manager *") ? (
                    <div
                      style={{
                        background: "#e0e0e0",
                        color: "#333",
                        padding: "4px 12px",
                        borderRadius: "16px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      All Selected
                    </div>
                  ) : (
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.id} label={option.label} />
                    ))
                  );
                }}
                renderInput={(params) => (
                  <>
                    <TextField
                      {...params}
                      onBlur={field.onBlur}
                      error={Boolean(error)}
                      label={`${label} ${required ? "*" : ""}`}
                    />
                    {error && (
                      <Typography
                        variant="caption"
                        sx={{ pl: "2px" }}
                        color="rgb(211, 47, 47)"
                      >
                        {error.message || (error as any)?.value?.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </>
          );
        }}
      />
    </>
  );
}

export default FormAutoCompleteWithSelectAll;
